import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Plus, Check, X, Info, ArrowRight, Settings2, RotateCcw, Layers, ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useEntitledFrameworks, useAddFramework, useRemoveFramework,
} from '@/api/hooks/useCtsFrameworks';
import { ManageFrameworksModal } from '@/features/compliance/frameworks/ManageFrameworksModal';
import {
  ConfirmFrameworkChange, type ConfirmKind,
} from '@/features/compliance/frameworks/ConfirmFrameworkChange';
import {
  scorableTotal, scorePercent,
  type EntitledFramework, type FrameworkProgress, type RetainedCounts,
} from '@/api/types/cts-frameworks.types';

/**
 * FRAMEWORKS — the tenant's programme workspace (Tenant Convergence TC3).
 *
 * ⚠ PROGRESS IS SHOWN FOR EVERY ENTITLED FRAMEWORK, ADOPTED OR NOT, and that is the point of the
 * page rather than a detail of it. Because progress is DERIVED from control work rather than
 * stored per framework, a tenant can see how far their existing work already carries them into a
 * standard they have not started. Adding changes the SCOPE of the Controls view, not the number.
 *
 * ⚠ AND NOTHING HERE EVER RENDERS 0% FOR AN UNMEASURED FRAMEWORK. `scorePercent` returns null when
 * there is nothing scorable, and null renders as "Not assessed" — a framework whose requirements
 * are all unmapped has not been failed at, it has not been measured.
 *
 * ⚠ THREE SECTIONS, BECAUSE "REMOVED" IS A PLACE, NOT AN EVENT. A toast after removal disappears;
 * the tenant who wants that framework back next quarter needs somewhere to find it. The toast
 * still fires — it carries the retained counts at the moment they are most reassuring — but the
 * durable answer is the section.
 */

const PANEL = 'rounded-[10px] border border-cert-neutral-border bg-bg-surface';
const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.11em] text-text-secondary';

/**
 * The decomposition bar. ⚠ The two segments that cannot fairly be scored — excluded and unmapped —
 * are drawn in neutral rather than omitted: hiding them would let a mostly-green bar sit over a
 * framework where nine of ten requirements have no control mapped to them at all.
 */
function ProgressBar({ p }: { p: FrameworkProgress }) {
  const segments = [
    { key: 'implemented', n: p.implemented, cls: 'bg-cert-valid-fg' },
    { key: 'partially_covered', n: p.partially_covered, cls: 'bg-teal-500/70' },
    { key: 'partially_implemented', n: p.partially_implemented, cls: 'bg-cert-warn-fg' },
    { key: 'not_implemented', n: p.not_implemented, cls: 'bg-cert-expired-fg' },
    { key: 'unknown', n: p.unknown, cls: 'bg-slate-500' },
    { key: 'not_started', n: p.not_started, cls: 'bg-text-faint/35' },
    { key: 'excluded_na', n: p.excluded_na, cls: 'bg-text-faint/20' },
    { key: 'unmapped', n: p.unmapped, cls: 'bg-text-faint/12' },
  ].filter((s) => s.n > 0);

  if (p.requirements_total === 0) {
    return <p className="text-[12px] italic text-text-faint">This framework has no published requirements yet.</p>;
  }
  return (
    <div className="flex h-[7px] w-full overflow-hidden rounded-full bg-bg-surface-2">
      {segments.map((s) => (
        <div key={s.key} className={cn('h-full', s.cls)}
          style={{ width: `${(s.n / p.requirements_total) * 100}%` }} />
      ))}
    </div>
  );
}

function Legend({ p }: { p: FrameworkProgress }) {
  const rows: [string, number, string][] = [
    // ⚠ REQUIREMENT VOCABULARY. A requirement is MET; a CONTROL is implemented. This legend
    //    said "Implemented / Partially implemented / Not implemented" about requirements, which
    //    merged two vocabularies for two different objects on a live surface.
    // ⚠ THE TWO PARTIALS STAY SEPARATE HERE, because the legend is the DETAIL: they collapse to
    //    one headline but lead to different next steps — map more controls, or finish the work.
    ['Met', p.implemented, 'bg-cert-valid-fg'],
    ['Partially met — coverage gap', p.partially_covered, 'bg-teal-500/70'],
    ['Partially met — implementation gap', p.partially_implemented, 'bg-cert-warn-fg'],
    ['Not met', p.not_implemented, 'bg-cert-expired-fg'],
    // ⚠ Both of these are NOT ASSESSED at the status axis — "nobody looked" and "somebody
    //    looked and could not tell" are two ways of having no determination, not two outcomes.
    ['Not assessed — inconclusive', p.unknown, 'bg-slate-500'],
    ['Not assessed — not started', p.not_started, 'bg-text-faint/35'],
    ['Not applicable', p.excluded_na, 'bg-text-faint/20'],
    ['Not yet mapped', p.unmapped, 'bg-text-faint/12'],
  ];
  return (
    <dl className="mt-[10px] grid grid-cols-2 gap-x-[16px] gap-y-[3px]">
      {rows.filter(([, n]) => n > 0).map(([label, n, cls]) => (
        <div key={label} className="flex items-baseline gap-[7px] text-[11.5px]">
          <span className={cn('h-[7px] w-[7px] shrink-0 rounded-[2px]', cls)} />
          <dt className="text-text-secondary">{label}</dt>
          <dd className="ml-auto tabular-nums text-text-primary">{n}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The brief confirmation after a removal. The persistent answer is the Removed section. */
function RetainedNote({ name, retained, onDismiss }: {
  name: string; retained: RetainedCounts; onDismiss: () => void;
}) {
  const parts = [
    [retained.controls, 'control'], [retained.evidence, 'evidence item'],
    [retained.assessments, 'assessment'], [retained.exceptions, 'exception'],
  ] as const;
  const kept = parts.filter(([n]) => n > 0).map(([n, w]) => `${n} ${w}${n === 1 ? '' : 's'}`);
  return (
    <div className={cn(PANEL, 'mb-[14px] flex items-start gap-[10px] px-[16px] py-[12px]')}>
      <Check className="mt-[2px] h-[14px] w-[14px] shrink-0 text-cert-valid-fg" aria-hidden="true" />
      <p className="flex-1 text-[12px] text-text-secondary">
        <span className="font-medium text-text-primary">{name}</span> was removed from your
        workspace.{' '}
        {kept.length > 0
          ? <>Your work was kept: <span className="text-text-primary">{kept.join(', ')}</span>. You
            can restore it from <span className="text-text-primary">Removed from workspace</span> below.</>
          : <>There was no recorded work on its controls. You can restore it below at any time.</>}
      </p>
      <button type="button" onClick={onDismiss} aria-label="Dismiss"
        className="text-text-faint transition-colors hover:text-text-primary">
        <X className="h-[13px] w-[13px]" aria-hidden="true" />
      </button>
    </div>
  );
}

function FrameworkCard({ f, onAdd, onRemove, busy }: {
  f: EntitledFramework; onAdd: () => void; onRemove: () => void; busy: boolean;
}) {
  const pct = scorePercent(f.progress);
  const scorable = scorableTotal(f.progress);
  const removed = f.status === 'removed';

  return (
    <article className={cn(PANEL, 'flex flex-col p-[16px]', removed && 'opacity-75')}>
      <header className="mb-[12px] flex items-start gap-[10px]">
        <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-bg-surface-2">
          <ShieldCheck className="h-[15px] w-[15px] text-accent-cyan" strokeWidth={1.5} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[14px] font-semibold text-text-primary">{f.name}</h2>
          <p className="mt-[2px] text-[11px] text-text-secondary">
            {[f.publisher, f.version_label].filter(Boolean).join(' · ') || f.display_code}
          </p>
        </div>
        {f.status === 'adopted' && (
          <span className="flex shrink-0 items-center gap-[5px] rounded-full bg-cert-valid-fg/12 px-[8px] py-[2px] text-[10.5px] font-medium text-cert-valid-fg">
            <Check className="h-[11px] w-[11px]" aria-hidden="true" /> In workspace
          </span>
        )}
      </header>

      <div className="mb-[4px] flex items-baseline gap-[8px]">
        {/*
          ⚠ NULL RENDERS AS "NOT ASSESSED", NEVER 0%. Nothing scorable means nothing measured —
          not a failure, and the two must never look the same.
        */}
        {pct === null ? (
          <span className="text-[19px] font-semibold italic text-text-faint">Not assessed</span>
        ) : (
          <>
            <span className="text-[22px] font-semibold tabular-nums text-text-primary">{pct}%</span>
            <span className="text-[11px] text-text-secondary">
              {/* ⚠ MET. This read "requirements implemented" — control vocabulary applied to a
                  requirement, in a sentence, on a live tenant surface. */}
              {f.progress.met} of {scorable} requirements met
            </span>
          </>
        )}
      </div>

      <ProgressBar p={f.progress} />
      <Legend p={f.progress} />

      {(f.progress.unmapped > 0 || f.progress.excluded_na > 0) && pct !== null && (
        <p className="mt-[10px] flex items-start gap-[6px] text-[11px] leading-[1.5] text-text-faint">
          <Info className="mt-[2px] h-[11px] w-[11px] shrink-0" aria-hidden="true" />
          <span>
            {[
              f.progress.unmapped > 0 ? `${f.progress.unmapped} not yet mapped to any control` : null,
              f.progress.excluded_na > 0 ? `${f.progress.excluded_na} marked not applicable` : null,
            ].filter(Boolean).join(' · ')}
            {' '}— excluded from the percentage.
          </span>
        </p>
      )}
      <div className="mb-[14px]" />

      {removed && (
        <p className="mb-[14px] mt-[10px] text-[11.5px] text-text-faint">
          Removed from your workspace. Your control work, evidence and assessments were kept.
        </p>
      )}

      {/*
        ⚠ ONE ACTION GEOMETRY ACROSS ALL THREE STATES: the way OUT of the card sits bottom-left,
        the action ON the card sits bottom-right. A tenant scanning a grid learns the position once
        instead of re-reading every card.
        ⚠ AND `mt-auto` PINS THE FOOTER TO THE BOTTOM OF THE CARD, NOT TO THE END OF ITS CONTENT.
        Cards in a row are the same height (grid stretches them), but their legends are not — a
        framework with eight posture buckets is taller than one with three. Without this the
        buttons land at different heights across a row, which is the misalignment MI spotted: the
        eye reads a ragged row as an inconsistency in the DATA before it blames the layout.
      */}
      <div className="mt-auto flex items-center gap-[10px] border-t border-border-subtle pt-[12px]">
        {f.status === 'adopted' && (
          <Link to="/compliance/workspace/controls"
            className="flex items-center gap-[5px] text-[12px] text-accent-cyan transition-opacity hover:opacity-80">
            Open controls <ArrowRight className="h-[12px] w-[12px]" aria-hidden="true" />
          </Link>
        )}
        {/* ⚠ JOINS "Open controls" rather than replacing it — two different questions with two
            different destinations. Controls are where the WORK is; the framework structure is
            where you see what the standard actually asks for and how far it is covered. */}
        <Link to={`/compliance/workspace/frameworks/${f.reference_id}`}
          className="flex items-center gap-[5px] text-[12px] text-accent-cyan transition-opacity hover:opacity-80">
          View framework <ArrowRight className="h-[12px] w-[12px]" aria-hidden="true" />
        </Link>
        {f.status === 'adopted' ? (
          <button type="button" onClick={onRemove} disabled={busy}
            className="ml-auto rounded-[7px] border border-cert-neutral-border px-[11px] py-[6px] text-[12px] text-text-secondary transition-colors hover:text-text-primary disabled:opacity-50">
            Remove from workspace
          </button>
        ) : (
          <button type="button" onClick={onAdd} disabled={busy}
            className="ml-auto flex items-center gap-[6px] rounded-[7px] bg-accent-cyan px-[13px] py-[7px] text-[12.5px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50">
            {removed
              ? <><RotateCcw className="h-[13px] w-[13px]" aria-hidden="true" /> Restore to workspace</>
              : <><Plus className="h-[13px] w-[13px]" aria-hidden="true" /> Add to workspace</>}
          </button>
        )}
      </div>
    </article>
  );
}

export function FrameworksWorkspacePage() {
  const { data: frameworks, isLoading, isError } = useEntitledFrameworks();
  const add = useAddFramework();
  const remove = useRemoveFramework();
  const [removedNote, setRemovedNote] = useState<{ name: string; retained: RetainedCounts } | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [confirm, setConfirm] = useState<{ kind: ConfirmKind; f: EntitledFramework } | null>(null);
  /**
   * ⚠ COLLAPSED BY DEFAULT, BECAUSE THE PAGE HAS ONE SUBJECT: what the tenant is working. Frameworks
   * they have not adopted are a decision for another day, and a grid of them pushes the actual
   * programme below the fold. The banner keeps the door visible rather than hiding it — a
   * collapsed section nobody notices is worse than no section at all.
   */
  const [browseOpen, setBrowseOpen] = useState(false);

  const busy = add.isPending || remove.isPending;

  if (isLoading) {
    return (
      <div className="space-y-[12px] p-[24px]">
        <Skeleton className="h-[28px] w-[220px]" />
        <Skeleton className="h-[220px] w-full" />
      </div>
    );
  }
  if (isError || !frameworks) {
    return <div className="p-[24px] text-[13px] text-text-secondary">Frameworks could not be loaded.</div>;
  }

  const adopted = frameworks.filter((f) => f.status === 'adopted');
  const available = frameworks.filter((f) => f.status === 'available');
  const removedList = frameworks.filter((f) => f.status === 'removed');
  /** ⚠ First-time = nothing adopted AND nothing ever removed. A tenant who cleared their */
  /*     workspace is not a first-time tenant, and must not be shown an onboarding screen. */
  const firstTime = adopted.length === 0 && removedList.length === 0;

  const commit = () => {
    if (!confirm) return;
    const { kind, f } = confirm;
    if (kind === 'add') {
      add.mutate(f.reference_id, { onSuccess: () => setConfirm(null) });
    } else {
      remove.mutate(f.reference_id, {
        onSuccess: (r) => {
          if (r.retained) setRemovedNote({ name: f.name, retained: r.retained });
          // ⚠ Open the section the toast points at. Telling someone their framework is "below"
          //    while it sits inside a collapsed panel is a direction that does not lead anywhere.
          setBrowseOpen(true);
          setConfirm(null);
        },
      });
    }
  };

  return (
    <div className="p-[24px]">
      <header className="mb-[18px] flex flex-wrap items-start gap-[16px]">
        <div className="min-w-0 flex-1">
          <h1 className="text-[21px] font-bold tracking-[-0.01em] text-text-primary">Frameworks</h1>
          <p className="mt-[4px] max-w-[76ch] text-[13px] text-text-secondary">
            The frameworks you are licensed for. Progress is calculated from the control work you
            have already done — so a framework shows how far it is already satisfied before you
            add it.
          </p>
        </div>
        {frameworks.length > 0 && (
          <button type="button" onClick={() => setManageOpen(true)}
            className="flex items-center gap-[6px] rounded-[7px] border border-cert-neutral-border bg-bg-surface px-[13px] py-[8px] text-[12.5px] text-text-secondary transition-colors hover:text-text-primary">
            <Settings2 className="h-[14px] w-[14px]" aria-hidden="true" /> Manage Frameworks
          </button>
        )}
      </header>

      {removedNote && (
        <RetainedNote name={removedNote.name} retained={removedNote.retained}
          onDismiss={() => setRemovedNote(null)} />
      )}

      {frameworks.length === 0 ? (
        <div className={cn(PANEL, 'flex items-start gap-[10px] px-[16px] py-[16px]')}>
          <ShieldCheck className="mt-[2px] h-[15px] w-[15px] shrink-0 text-text-faint" aria-hidden="true" />
          <p className="text-[13px] text-text-secondary">
            No frameworks are licensed to your organisation yet.
          </p>
        </div>
      ) : firstTime ? (
        /*
          ⚠ THE FIRST-TIME STATE IS A DESTINATION, NOT AN EMPTY GRID. A licensed tenant who has
          added nothing has one thing to do, and a page of unadopted tiles buries it among choices
          they cannot yet evaluate. It is deliberately NOT shown to a tenant who cleared their
          workspace — they have used this before, and being onboarded again would misread them.
        */
        <div className={cn(PANEL, 'flex flex-col items-center px-[24px] py-[56px] text-center')}>
          <span className="mb-[16px] flex h-[46px] w-[46px] items-center justify-center rounded-[12px] bg-bg-surface-2">
            <Layers className="h-[21px] w-[21px] text-accent-cyan" strokeWidth={1.4} aria-hidden="true" />
          </span>
          <h2 className="text-[17px] font-semibold text-text-primary">Build your compliance program</h2>
          <p className="mt-[7px] max-w-[52ch] text-[13px] leading-[1.6] text-text-secondary">
            Add a framework to your workspace to begin tracking requirements, mapped controls, and
            progress.
          </p>
          <button type="button" onClick={() => setManageOpen(true)}
            className="mt-[20px] flex items-center gap-[7px] rounded-[8px] bg-accent-cyan px-[18px] py-[10px] text-[13px] font-medium text-white transition-opacity hover:opacity-90">
            <Plus className="h-[14px] w-[14px]" aria-hidden="true" /> Add your first framework
          </button>
          <p className="mt-[12px] text-[11.5px] text-text-faint">
            {frameworks.length} {frameworks.length === 1 ? 'framework is' : 'frameworks are'} licensed to your organisation.
          </p>
        </div>
      ) : (
        <div className="space-y-[22px]">
          {adopted.length > 0 && (
            <section>
              <h2 className={cn(EYEBROW, 'mb-[9px] block')}>In your workspace</h2>
              <div className="grid gap-[12px] lg:grid-cols-2 2xl:grid-cols-3">
                {adopted.map((f) => (
                  <FrameworkCard
                    key={f.reference_id} f={f} busy={busy}
                    onAdd={() => setConfirm({ kind: 'add', f })}
                    onRemove={() => setConfirm({ kind: 'remove', f })}
                  />
                ))}
              </div>
            </section>
          )}

          {(available.length > 0 || removedList.length > 0) && (
            <section className={cn(PANEL, 'overflow-hidden')}>
              {/*
                ⚠ THE BANNER NAMES BOTH COUNTS SEPARATELY, EVEN WHILE COLLAPSED. "3 more
                frameworks" would merge two facts the whole model keeps apart: one the tenant has
                never tried, one they used and stepped away from with their work still intact. The
                distinction has to survive the collapse, or collapsing destroys it.
              */}
              {/*
                ⚠ ONE LINE, NOT A BANNER. The page's subject is what the tenant is WORKING; anything
                they have not adopted is a decision for another day, and even a well-made banner
                spends vertical space arguing for that decision every time the page loads. A single
                summary row keeps the door visible while taking almost none of the page.
              */}
              <button
                type="button"
                onClick={() => setBrowseOpen((v) => !v)}
                aria-expanded={browseOpen}
                aria-controls="framework-browse"
                className="flex w-full items-center gap-[10px] px-[16px] py-[11px] text-left transition-colors hover:bg-bg-surface-2"
              >
                <Layers className="h-[14px] w-[14px] shrink-0 text-accent-cyan" strokeWidth={1.5} aria-hidden="true" />
                {/*
                  ⚠ BOTH COUNTS ARE NAMED SEPARATELY, EVEN COMPRESSED TO ONE LINE. "3 frameworks"
                  would merge one the tenant never tried with one they used and stepped away from —
                  the distinction the rest of the model works to keep. Compression must not cost it.
                */}
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-text-secondary">
                  {[
                    available.length > 0
                      ? `${available.length} ${available.length === 1 ? 'framework' : 'frameworks'} available`
                      : null,
                    removedList.length > 0 ? `${removedList.length} removed` : null,
                  ].filter(Boolean).join(' · ')}
                </span>
                {/*
                  ⚠ "Show all", NOT "Manage frameworks" — the header button already carries that
                  name and opens the management WINDOW. Two controls wearing one name while doing
                  different things is a collision the reader has to resolve by clicking, and the
                  cost lands on whoever is least sure which they wanted.
                */}
                <span className="flex shrink-0 items-center gap-[5px] text-[12.5px] font-medium text-accent-cyan">
                  {browseOpen ? 'Hide' : 'Show all'}
                  <ChevronDown className={cn('h-[13px] w-[13px] transition-transform', browseOpen && 'rotate-180')} aria-hidden="true" />
                </span>
              </button>

              {browseOpen && (
                <div id="framework-browse" className="space-y-[20px] border-t border-border-subtle px-[16px] py-[16px]">
                  {([
                    ['Available to add', available],
                    ['Removed from workspace', removedList],
                  ] as const).filter(([, list]) => list.length > 0).map(([title, list]) => (
                    <div key={title}>
                      <h2 className={cn(EYEBROW, 'mb-[9px] block')}>{title}</h2>
                      <div className="grid gap-[12px] xl:grid-cols-2">
                        {list.map((f) => (
                          <FrameworkCard
                            key={f.reference_id} f={f} busy={busy}
                            onAdd={() => setConfirm({ kind: 'add', f })}
                            onRemove={() => setConfirm({ kind: 'remove', f })}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {manageOpen && (
        <ManageFrameworksModal
          frameworks={frameworks} busy={busy}
          onClose={() => setManageOpen(false)}
          // ⚠ The chooser hands off to the SAME confirmation the tiles use. Two entry points, one
          //   decision surface — so the licensing statement cannot be reachable by one path only.
          onAdd={(f) => { setManageOpen(false); setConfirm({ kind: 'add', f }); }}
          onRemove={(f) => { setManageOpen(false); setConfirm({ kind: 'remove', f }); }}
        />
      )}

      {confirm && (
        <ConfirmFrameworkChange
          kind={confirm.kind} frameworkName={confirm.f.name} busy={busy}
          onCancel={() => setConfirm(null)} onConfirm={commit}
        />
      )}
    </div>
  );
}
